/**
 * What is owed to us, by whom, and how late.
 *
 * The mirror of the payables screen, and derived the same way: from the
 * documents, on every read, with the ledger's own answer shown beside it. Two
 * numbers that can disagree eventually will, and the cached one is always the
 * one people act on.
 *
 * The expected-deduction column is the part that is not in payables. An insurer
 * settles net of a panel discount, so part of a receivable will never arrive —
 * and a list that shows the gross alone reads as money that is coming.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { Card, ErrorNote, Loading, Note } from '../components/ui'

interface PartyAgeing {
  party_id: string
  party_name: string
  buckets: Record<string, number>
  total_paise: number
  expected_deduction_paise: number
}

interface Ageing {
  as_at: string
  rows: PartyAgeing[]
  totals: Record<string, number>
  control_account_paise: number | null
  ties_out: boolean
}

interface OpenInvoice {
  invoice_id: string
  reference: string
  party_id: string
  party_name: string
  invoice_date: string
  due_date: string
  total_paise: number
  received_paise: number
  outstanding_paise: number
  expected_deduction_paise: number
  days_overdue: number
  bucket: string
}

const BUCKETS: { key: string; label: string }[] = [
  { key: 'not_due', label: 'Not due' },
  { key: '1_30', label: '1–30 days' },
  { key: '31_60', label: '31–60' },
  { key: '61_90', label: '61–90' },
  { key: 'over_90', label: 'Over 90' },
]

export default function Receivables() {
  const [party, setParty] = useState<string | null>(null)

  const ageing = useQuery({
    queryKey: ['ar-ageing'],
    queryFn: () => api.get<Ageing>('/receivables/ageing'),
  })
  const invoices = useQuery({
    queryKey: ['ar-open-invoices'],
    queryFn: () => api.get<OpenInvoice[]>('/receivables/open-invoices'),
  })

  if (ageing.isLoading) return <Loading what="Working out what is owed to us" />
  const data = ageing.data
  const rows = (invoices.data ?? []).filter((i) => !party || i.party_id === party)
  const subledger = data ? Object.values(data.totals).reduce((a, b) => a + b, 0) : 0
  const expected = (invoices.data ?? []).reduce(
    (a, i) => a + i.expected_deduction_paise,
    0,
  )

  return (
    <>
      <ErrorNote error={ageing.error ?? invoices.error} />

      {data && data.control_account_paise === null && (
        <Note kind="warn">
          No account is mapped to the receivables control role, so the list below
          cannot be checked against the ledger. The figures are what the documents
          say. Map an account in the chart of accounts to close the loop.
        </Note>
      )}

      {data && data.control_account_paise !== null && !data.ties_out && (
        <Note kind="warn">
          The open invoices come to {formatInr(subledger)} but the receivables control
          account says {formatInr(data.control_account_paise)}. Journals posted straight
          to receivables explain most of a difference like this. Anything left over means
          a receipt is allocated to an invoice it did not settle.
        </Note>
      )}

      <Card
        title="What is owed to us"
        hint={data ? `as at ${data.as_at}` : undefined}
        tight
        actions={
          data?.ties_out ? (
            <span className="faint">agrees with the ledger</span>
          ) : undefined
        }
      >
        {!data || data.rows.length === 0 ? (
          <div className="empty">Nothing outstanding.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                {BUCKETS.map((b) => (
                  <th key={b.key} className="num">
                    {b.label}
                  </th>
                ))}
                <th className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.party_id}>
                  <td>
                    <button className="small" onClick={() => setParty(row.party_id)}>
                      {row.party_name}
                    </button>
                    {row.expected_deduction_paise > 0 && (
                      <div className="faint">
                        {formatInr(row.expected_deduction_paise)} expected to be
                        deducted
                      </div>
                    )}
                  </td>
                  {BUCKETS.map((b) => (
                    <td key={b.key} className="num">
                      {row.buckets[b.key] ? (
                        formatInr(row.buckets[b.key])
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                  ))}
                  <td className="num">
                    <strong>{formatInr(row.total_paise)}</strong>
                  </td>
                </tr>
              ))}
              <tr>
                <td>
                  <strong>Total</strong>
                </td>
                {BUCKETS.map((b) => (
                  <td key={b.key} className="num">
                    <strong>{formatInr(data.totals[b.key] ?? 0)}</strong>
                  </td>
                ))}
                <td className="num">
                  <strong>{formatInr(subledger)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </Card>

      {expected > 0 && (
        <Note kind="info">
          {formatInr(expected)} of what is outstanding is expected to be deducted before
          it is paid — panel discounts, co-pays and tax withheld by the payer. What will
          realistically arrive is {formatInr(subledger - expected)}.
        </Note>
      )}

      <Card
        title="Open invoices"
        hint={`${rows.length}${party ? ' — filtered' : ''}`}
        tight
        actions={
          party ? (
            <button className="small" onClick={() => setParty(null)}>
              Show all
            </button>
          ) : undefined
        }
      >
        {rows.length === 0 ? (
          <div className="empty">Nothing outstanding.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Customer</th>
                <th>Due</th>
                <th className="num">Overdue</th>
                <th className="num">Invoice</th>
                <th className="num">Received</th>
                <th className="num">Outstanding</th>
                <th className="num">Expected deduction</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.invoice_id}>
                  <td className="mono">{row.reference}</td>
                  <td>{row.party_name}</td>
                  <td className="muted date">{row.due_date}</td>
                  <td className="num">
                    {row.days_overdue > 0 ? (
                      `${row.days_overdue}d`
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td className="num muted">{formatInr(row.total_paise)}</td>
                  <td className="num muted">
                    {row.received_paise ? (
                      formatInr(row.received_paise)
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td className="num">{formatInr(row.outstanding_paise)}</td>
                  <td className="num muted">
                    {row.expected_deduction_paise ? (
                      formatInr(row.expected_deduction_paise)
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  )
}
